import { useEffect, useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Typography,
  Grid,
} from '@mui/material';
import {
  createSubjectDirect,
  updateSubjectDirect,
  getSubjectsDirect,
} from '@timetable/api';
import { useAuthStore } from '../../store/authStore';
import type { Subject, CreateSubjectRequest, UpdateSubjectRequest } from '@timetable/types';
import { queryKeys } from '../../lib/queryKeys';

interface SubjectFormDialogProps {
  open: boolean;
  subject: Subject | null;
  onClose: () => void;
}

// Predefined color palette for subjects
const COLOR_PALETTE = [
  '#F44336', // Red
  '#E91E63', // Pink
  '#9C27B0', // Purple
  '#673AB7', // Deep Purple
  '#3F51B5', // Indigo
  '#2196F3', // Blue
  '#03A9F4', // Light Blue
  '#00BCD4', // Cyan
  '#009688', // Teal
  '#4CAF50', // Green
  '#8BC34A', // Light Green
  '#CDDC39', // Lime
  '#FFEB3B', // Yellow
  '#FFC107', // Amber
  '#FF9800', // Orange
  '#FF5722', // Deep Orange
  '#795548', // Brown
  '#9E9E9E', // Grey
  '#607D8B', // Blue Grey
];

export function SubjectFormDialog({ open, subject, onClose }: SubjectFormDialogProps) {
  const queryClient = useQueryClient();
  const schoolContext = useAuthStore((state) => state.schoolContext);
  const isEditMode = !!subject;

  // Form state
  const [subjectName, setSubjectName] = useState('');
  const [subjectCode, setSubjectCode] = useState('');
  const [colorCode, setColorCode] = useState('');
  const [customColor, setCustomColor] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // Fetch all subjects for uniqueness validation
  const { data: allSubjects = [] } = useQuery({
    queryKey: queryKeys.subjects.list({ school_id: schoolContext || '' }),
    queryFn: () => getSubjectsDirect({ school_id: schoolContext || undefined }),
    enabled: !!schoolContext && open,
  });

  // Initialize form with subject data
  useEffect(() => {
    if (subject) {
      setSubjectName(subject.subject_name);
      setSubjectCode(subject.subject_code || '');
      setColorCode(subject.color_code || '');
      setCustomColor('');
    } else {
      setSubjectName('');
      setSubjectCode('');
      setColorCode('');
      setCustomColor('');
    }
    setValidationError(null);
  }, [subject, open]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateSubjectRequest) => createSubjectDirect(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subjects.all });
      onClose();
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateSubjectRequest) => updateSubjectDirect(subject!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subjects.all });
      onClose();
    },
  });

  const validateForm = (): boolean => {
    // Validate subject name
    if (!subjectName.trim()) {
      setValidationError('Subject name is required');
      return false;
    }

    // Validate subject name uniqueness (case-insensitive)
    const nameExists = allSubjects.some(
      (s) =>
        s.subject_name.trim().toLowerCase() === subjectName.trim().toLowerCase() &&
        s.id !== subject?.id
    );
    if (nameExists) {
      setValidationError('Subject name already exists. Please use a unique name.');
      return false;
    }

    // Validate subject code uniqueness
    if (subjectCode.trim()) {
      const codeExists = allSubjects.some(
        (s) =>
          s.subject_code?.toLowerCase() === subjectCode.trim().toLowerCase() &&
          s.id !== subject?.id
      );
      if (codeExists) {
        setValidationError('Subject code already exists. Please use a unique code.');
        return false;
      }
    }

    // Validate color code format if custom color is provided
    const finalColor = customColor || colorCode;
    if (finalColor && !/^#[0-9A-Fa-f]{6}$/.test(finalColor)) {
      setValidationError('Invalid color format. Use hex format like #FF5722');
      return false;
    }

    setValidationError(null);
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const finalColor = customColor || colorCode || null;

    if (isEditMode) {
      updateMutation.mutate({
        subject_name: subjectName.trim(),
        subject_code: subjectCode.trim() || undefined,
        color_code: finalColor || undefined,
      });
    } else {
      if (!schoolContext) {
        setValidationError('School context not found');
        return;
      }

      createMutation.mutate({
        school_id: schoolContext,
        subject_name: subjectName.trim(),
        subject_code: subjectCode.trim() || undefined,
        color_code: finalColor || undefined,
      });
    }
  };

  const handleColorSelect = (color: string) => {
    setColorCode(color);
    setCustomColor('');
  };

  const handleCustomColorChange = (value: string) => {
    setCustomColor(value);
    setColorCode('');
  };

  const mutation = isEditMode ? updateMutation : createMutation;
  const selectedColor = customColor || colorCode;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Subject' : 'Add Subject'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {validationError && (
            <Alert severity="error" onClose={() => setValidationError(null)}>
              {validationError}
            </Alert>
          )}

          {mutation.error && (
            <Alert severity="error">
              {(mutation.error as Error).message}
            </Alert>
          )}

          <TextField
            label="Subject Name"
            value={subjectName}
            onChange={(e) => {
              // Capitalize first letter of each word to prevent case-based duplicates
              const val = e.target.value;
              const normalized = val.replace(/\b\w/g, (c) => c.toUpperCase());
              setSubjectName(normalized);
            }}
            required
            fullWidth
            autoFocus
            placeholder="e.g., Mathematics, English, Science"
          />

          <TextField
            label="Subject Code"
            value={subjectCode}
            onChange={(e) => setSubjectCode(e.target.value.toUpperCase())}
            fullWidth
            placeholder="e.g., MATH, ENG, SCI"
            helperText="Optional. Must be unique if provided."
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Subject Color
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a color to visually identify this subject in the timetable
            </Typography>

            <Grid container spacing={1} sx={{ mb: 2 }}>
              {COLOR_PALETTE.map((color) => (
                <Grid item key={color}>
                  <Box
                    onClick={() => handleColorSelect(color)}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      backgroundColor: color,
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: selectedColor === color ? 'primary.main' : 'transparent',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: 2,
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>

            <TextField
              label="Custom Color (Hex)"
              value={customColor}
              onChange={(e) => handleCustomColorChange(e.target.value)}
              fullWidth
              placeholder="#FF5722"
              helperText="Or enter a custom hex color code"
              InputProps={{
                startAdornment: customColor && (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: 1,
                      backgroundColor: customColor,
                      border: '1px solid',
                      borderColor: 'divider',
                      mr: 1,
                    }}
                  />
                ),
              }}
            />
          </Box>

          {selectedColor && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Selected color:
              </Typography>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  backgroundColor: selectedColor,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
              <Typography variant="body2" fontFamily="monospace">
                {selectedColor}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
